import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Copy } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Shown in the report so a crash can be traced to where it happened. */
  area?: string;
}

interface State {
  error: Error | null;
  componentStack: string;
  copied: boolean;
}

/**
 * Catches render errors so a crash shows something useful instead of a blank page.
 *
 * Without this, any unhandled error unmounts the entire React tree and leaves a
 * white screen with no message — the error exists only in that user's console,
 * so diagnosing it means asking them to open devtools and relay it.
 *
 * The error is also POSTed to the backend, which logs it, so a crash is visible
 * in the pod log without anyone having to report it.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: '', copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const componentStack = info.componentStack || '';
    this.setState({ componentStack });

    // Best effort: a failure to report must never make the failure worse.
    try {
      // PMO authenticates with a Bearer token, not a cookie — `credentials:
      // include` produced a 401 and the report never arrived. Read the token
      // straight from storage rather than importing the auth module: if that
      // module is what crashed, importing it here would fail too.
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      try {
        const stored = localStorage.getItem('hs_auth_tokens');
        const parsed = stored ? JSON.parse(stored) : null;
        // Stored as `access_token`; the camelCase spelling is accepted too so a
        // later rename of the token set does not silently stop reporting.
        const accessToken = parsed?.access_token || parsed?.accessToken || '';
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      } catch {
        /* an unreportable error is still better than a second crash */
      }

      void fetch('/api/client-errors', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: `${error.name}: ${error.message}`,
          stack: error.stack || '',
          componentStack,
          url: window.location.href,
          appVersion: (import.meta as any)?.env?.VITE_APP_VERSION || '',
        }),
      }).catch(() => undefined);
    } catch {
      /* reporting is optional */
    }

    // Still log locally so it is in the console for anyone already looking.
    console.error(`[ErrorBoundary${this.props.area ? `: ${this.props.area}` : ''}]`,
                  error, componentStack);
  }

  private reset = () => this.setState({ error: null, componentStack: '', copied: false });

  private copy = () => {
    const { error, componentStack } = this.state;
    const text = [
      `${error?.name}: ${error?.message}`,
      `URL: ${window.location.href}`,
      '',
      error?.stack || '',
      '',
      '--- component stack ---',
      componentStack,
    ].join('\n');
    navigator.clipboard?.writeText(text).then(
      () => this.setState({ copied: true }),
      () => undefined,
    );
  };

  render() {
    const { error, componentStack, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex items-center justify-center h-full p-6 overflow-y-auto">
        <div className="max-w-2xl w-full rounded-xl border border-red-200 dark:border-red-900 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-start gap-3 p-5 border-b border-gray-200 dark:border-gray-700">
            <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={20} />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Something broke on this screen
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                The rest of the app is fine — this view failed to render. The
                error has been reported automatically.
              </p>
            </div>
          </div>

          <div className="p-5 space-y-3">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 font-mono text-xs text-red-600 dark:text-red-400 break-words">
              {error.name}: {error.message}
            </div>

            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600 dark:text-gray-400 select-none">
                Technical detail
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-[11px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
{error.stack || '(no stack)'}
{componentStack ? `\n--- component stack ---${componentStack}` : ''}
              </pre>
            </details>

            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={this.reset}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-accent text-white hover:bg-accent/90"
              >
                <RefreshCw size={14} /> Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              >
                Reload the page
              </button>
              <button
                onClick={this.copy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 ml-auto"
              >
                <Copy size={14} /> {copied ? 'Copied' : 'Copy details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
